import { type ComponentType, useContext } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { getJobPickerState, getSelectedJobId } from "utilities/jobPicker/jobPicker";

import { type IResearchPreviewPageProps } from "./ResearchPreviewLayout.types";

/**
 * Builds the props `ListPageLayout` would pass its page. The shell has no job picker, so these
 * come synchronously from the selection the v1 picker persisted.
 *
 * Unlike `ListPageLayout`, a single job's `jobName` is empty (All Jobs uses `allJobsText`) and
 * the builder's General job is never included. With no selection the page still renders, and
 * shows its own "select a jobsite" state.
 */
const useShellListPageChildProps = (allJobsText?: string): IResearchPreviewPageProps => {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const builderId = builderInfo?.builderId ?? 0;

    // Drop the AllJobs (0) and NoJobs (-1) sentinels; All Jobs persists every selected id.
    const { selectedJobIds, isAllJobsSelected, isTemplateMode } = getJobPickerState();
    let jobIds = selectedJobIds.filter((id) => id > 0);
    if (jobIds.length === 0) {
        const selectedJobId = getSelectedJobId(builderId);
        if (selectedJobId > 0) {
            jobIds = [selectedJobId];
        }
    }

    return {
        builderId,
        jobIds,
        jobName: isAllJobsSelected ? (allJobsText ?? "All Jobs") : "",
        userId: userInfo?.globalUserId ?? 0,
        isTemplateMode,
        loginType: userInfo?.loginType ?? BTLoginTypes.BUILDER,
        jobPickerReady: true,
        isAllJobsSelected,
        jobPickerCount: jobIds.length,
        setJobPickerCollapsed: () => undefined,
    };
};

interface IResearchPreviewShellCanvasProps {
    researchComponent: ComponentType<IResearchPreviewPageProps>;
    allJobsText?: string;
}

/**
 * Renders a route's `researchComponent` in `AppShell`'s canvas, with the props `ListPageLayout`
 * would have passed its page.
 */
export const ResearchPreviewShellCanvas = ({
    researchComponent: ResearchComponent,
    allJobsText,
}: IResearchPreviewShellCanvasProps) => {
    const listLayoutProps = useShellListPageChildProps(allJobsText);
    return <ResearchComponent {...listLayoutProps} />;
};
